import React, { useEffect, useState } from "react";
import { 
  Box, 
  Container, 
  Typography, 
  Button,
  CircularProgress
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../firebase";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../firebase";
import { Missionary } from "../../Account/Mission/MissionaryArtTab";
import MissionarySelector from "./MissionarySelector";
import MissionaryEditor from "./MissionaryEditor";

const BG_IMAGE = "https://media.rainpos.com/663/hex5f021f_burgundy_design_swirl_boldtiny09867633333333.jpg";

const MissionaryStudioPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");
  const mode = searchParams.get("mode"); // 'select' or undefined
  
  const [loading, setLoading] = useState(true);
  const [activeMissionaryId, setActiveMissionaryId] = useState<string | null>(null);
  const [missionaries, setMissionaries] = useState<Missionary[]>([]);
  const [isLocked, setIsLocked] = useState(false);

  useEffect(() => {
    const init = async () => {
      if (!user) return;
      
      try {
        // 1. Fetch User's Global Missionaries
        const userDoc = await getDoc(doc(db, "users", user.uid));
        const userMissionaries = (userDoc.data()?.missionaries as Missionary[]) || [];
        setMissionaries(userMissionaries);

        // 2. If OrderID exists, check if it's already linked
        if (orderId) {
          const orderDoc = await getDoc(doc(db, "orders", orderId));
          if (orderDoc.exists()) {
             const data = orderDoc.data();
             
             // Check Status for Locking
             const status = data.status || 'pending';
             if (status !== 'pending' && status !== 'awaiting-info') {
                 setIsLocked(true);
             } else {
                 setIsLocked(false);
             }

             // Auto-Jump to Editor Logic
             // Only auto-jump if NOT in 'select' mode
             if (data.linkedMissionaryId && mode !== 'select') {
                setActiveMissionaryId(data.linkedMissionaryId);
             }
          }
        }
      } catch (err) {
        console.error("Error initializing missionary studio", err);
      } finally {
        setLoading(false);
      }
    };
    
    init();
  }, [user, orderId]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
  }

  // View State 1: Editing a specific missionary (either linked from order or manually selected)
  // View State 2: Selecting a missionary for an order
  // View State 3: Generic management (viewing list) - Reuses Selector UI but without 'select' action maybe? 
  // For now, let's treat "Selector" as the default view provided it handles "Select" logic internally.

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "#f5f5f5" }}>
      {/* Header */}
      <Box sx={{ 
        height: "120px", 
        backgroundImage: `url('${BG_IMAGE}')`, 
        backgroundSize: "cover",
        display: "flex", alignItems: "center"
      }}>
        <Container maxWidth="xl">
            <Button 
              startIcon={<ArrowBack />} 
              sx={{ color: "white", mb: 1, opacity: 0.8 }}
              onClick={() => {
                if (activeMissionaryId) {
                    // Back to Selection if we were editing
                    setActiveMissionaryId(null);
                } else {
                    // Back to Dashboard
                    navigate("/account");
                }
              }}
            >
              {activeMissionaryId ? "All Missionaries" : "Back to Studio"}
            </Button>
            <Typography variant="h4" sx={{ fontFamily: '"Playfair Display", serif', color: "white" }}>
              Missionary Studio
            </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="xl" sx={{ flexGrow: 1, p: 4 }}>
        {activeMissionaryId ? (
          <MissionaryEditor 
            missionaryId={activeMissionaryId} 
            orderId={orderId || undefined}
            isLocked={isLocked}
            onSave={() => {
               // Optional: maybe show success or navigate back? for now just stay
            }}
          />
        ) : (
          <MissionarySelector 
            missionaries={missionaries}
            orderId={orderId || ""} // If empty, Selector behaves in "Manager Mode" (logic to be refined)
            onSelect={(id) => setActiveMissionaryId(id)}
          />
        )}
      </Container>
    </Box>
  );
};

export default MissionaryStudioPage;
